import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Layout } from 'ui/layout/Layout'
import { AppBarAdmin } from 'ui/partials/AppBar'
import AdminContentWrapper from 'ui/layout/AdminContentWrapper'
import AdminSidebar, {
  AdminSidebarSelectRouteProps,
} from 'ui/partials/AdminSidebar'
import type {
  BankTransfer,
  Billing,
  BillingForm,
  InformationToMed,
  MedicalInstitutionProps,
  ReservationDetailProps,
} from 'types/med'
import type { CallMethodVideoAppNames } from 'types/common'

import ReservationFooterControls from './ReservationFooterControls'
import SidebarReservationList from './SidebarReservationList'
import type { SidebarReservationListProps } from './SidebarReservationList'
import ReservationDetail from './ReservationDetail'
import ReservationBillingModal from './ReservationBillingModal'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'
import Snackbar from 'ui/utils/Snackbar'
import {
  PhrIntegrationProps,
  ShippingProps,
} from './ReservationDetail/ProfileSection'
import type { ReservationDateModalProps } from './ReservationDateModal'
import { DepartmentListItemProps } from 'types/common'

export type ReservationsProps = {
  //TODO: 閲覧モードと編集モードでプロパティをまとめた方がいい?またはeditとreadでコンポーネントを分ける

  // 医療機関設定
  med: MedicalInstitutionProps
  // 予約詳細
  reservation?: ReservationDetailProps | null
  // 予約概要リスト
  reservationSubmenu: SidebarReservationListProps
  // 編集モードかどうか
  editable: boolean
  // キャンセルモーダルでsubmit時
  onSubmitCancel: () => Promise<void>
  // 通話完了をチェックした時
  onCheckMeetingFinished: (result: boolean) => void
  // ビデオ通話をクリックした時
  onStartVideoMeeting: (
    app: CallMethodVideoAppNames
  ) => Promise<void> | undefined
  // サイドバーでのページ切り替え
  selectRoute: AdminSidebarSelectRouteProps
  // 支払い関連
  billing: {
    billings?: Billing[]
    bankTransfer?: BankTransfer
  }
  // 支払い時のハンドラ
  onSubmitBilling: (billing: BillingForm) => Promise<void>
  onReSendReservationMail: () => Promise<void>
  shipping: ShippingProps
  phrIntegration: PhrIntegrationProps
  onChangeReservationDatetime: ReservationDateModalProps['onSubmit']
  departmentSetting?: DepartmentListItemProps
  update?: InformationToMed
}

const Reservations: React.FC<ReservationsProps> = ({
  med,
  reservation,
  selectRoute,
  reservationSubmenu,
  editable,
  onSubmitCancel,
  onCheckMeetingFinished,
  onStartVideoMeeting,
  billing,
  onSubmitBilling,
  onReSendReservationMail,
  shipping,
  phrIntegration,
  onChangeReservationDatetime,
  departmentSetting,
  update,
}: ReservationsProps) => {
  const { t } = useTranslation()
  const [billingModalOpen, setBillingModalOpen] = useState(false)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)

  const [mailSending, setMailSending] = useState<boolean>(false)
  const [snackBar, setSnackBar] = useState<{
    open: boolean
    message: string
    error: boolean
  }>({ open: false, message: '', error: false })

  const sidebarContent = (
    <AdminSidebar med={med} selectRoute={selectRoute}>
      <SidebarReservationList {...reservationSubmenu} />
    </AdminSidebar>
  )

  const cancelModal = (
    <Modal open={cancelModalOpen} handleClose={() => setCancelModalOpen(false)}>
      <ConfirmContent
        onCancel={() => setCancelModalOpen(false)}
        onSubmit={() => {
          onSubmitCancel().finally(() => setCancelModalOpen(false))
        }}
        title={t('予約を取り消しますか？')}
        submitLabel={t('取り消す')}
      />
    </Modal>
  )

  const controlBarContent = reservation ? (
    <ReservationFooterControls
      reservationStatus={reservation.status}
      billingStatus={reservation.settlement_status}
      settlement={reservation.settlement_method}
      callMethod={reservation.call_method}
      consultationMethod={reservation?.consultation_method}
      onClickCancel={() => setCancelModalOpen(true)}
      onClickStartVideoMeeting={onStartVideoMeeting}
      onCheckMeetingFinished={onCheckMeetingFinished}
      onClickBilling={() => setBillingModalOpen(true)}
      startTime={reservation.reservation_datetime_start}
      sending={mailSending}
      onClickReSendReservationMail={() => {
        setMailSending(true)
        onReSendReservationMail()
          .then(() => {
            setSnackBar({
              open: true,
              message: t('メール送信が完了しました'),
              error: false,
            })
          })
          .catch(() => {
            setSnackBar({
              open: true,
              message: t(
                'errors.送信できませんでした。もう一度送信してください。'
              ),
              error: true,
            })
          })
          .finally(() => setMailSending(false))
      }}
    />
  ) : null

  return (
    <Layout header={<AppBarAdmin update={update} />}>
      <AdminContentWrapper
        sidebar={sidebarContent}
        controlBar={controlBarContent}
      >
        <ReservationDetail
          departmentSetting={departmentSetting}
          onChangeReservationDatetime={(data) =>
            onChangeReservationDatetime(data)
              .then(() => {
                setSnackBar({
                  open: true,
                  message: t('予約変更が完了しました。'),
                  error: false,
                })
              })
              .catch(() => {
                setSnackBar({
                  open: true,
                  message: t('errors.日時が変更できませんでした。'),
                  error: true,
                })
              })
          }
          reservation={reservation}
          editable={editable}
          billings={billing.billings}
          bankTransfer={billing.bankTransfer}
          shipping={shipping}
          phrIntegration={phrIntegration}
          hasOnlineEligibilityVerificationUrl={
            !!med?.online_eligibility_verification_url
          }
        />
        {reservation && reservation.id && reservation.settlement_method && (
          <ReservationBillingModal
            billings={billing.billings || []}
            bankTransfer={billing.bankTransfer}
            isConnectedStripe={!!med.is_connected_stripe}
            settlement={reservation.settlement_method}
            onSubmit={(billing) =>
              onSubmitBilling(billing).then(() => setBillingModalOpen(false))
            }
            onCancel={() => {
              setBillingModalOpen(false)
            }}
            hasOnlineEligibilityVerificationUrl={
              !!med?.online_eligibility_verification_url
            }
            open={billingModalOpen}
          />
        )}
        <Snackbar
          open={snackBar.open}
          handleClose={() => setSnackBar((prev) => ({ ...prev, open: false }))}
          error={snackBar.error}
          message={snackBar.message}
        />
        {cancelModal}
      </AdminContentWrapper>
    </Layout>
  )
}
export default Reservations
